import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { find, reduce, get } from 'lodash';

import communicationQueuesResource from 'src/actions/communicationQueues/communicationQueuesResource';
import endpointsResource from 'src/actions/endpoints/endpointsResource';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import smsConfigurationResource from 'src/actions/channels/smsConfigurationResource';
import useResource, {
  getConsolidatedState,
  someFailed,
  STATUS_FAILED,
  useResourceValues,
} from 'src/presentation/hooks/useResource';
import { toastError } from 'src/actions/toast';

export const ENDPOINT_TYPE_SMS = 'SMS';

type SmsConfig = {
  id: string;
  number: string;
  inbox: string;
  name: string;
};

export const useSms = () => {
  const dispatch = useDispatch();

  const {
    values: smsConfigurations,
    loadingState: smsLoadingState,
    errors: smsErrors,
  } = useResource(smsConfigurationResource);
  const { values: communicationQueues, loadingState: communicationQueuesState } =
    useResourceValues(communicationQueuesResource);
  const { values: inboxes, loadingState: inboxesState } = useResourceValues(routingGroupsResource);

  const fetchError = someFailed(smsLoadingState, communicationQueuesState, inboxesState);
  const state = fetchError
    ? STATUS_FAILED
    : getConsolidatedState([smsLoadingState, communicationQueuesState, inboxesState]);

  const endpointsCommunicationQueueName = reduce(
    smsConfigurations,
    (acc, smsConfig) => {
      const commQueue = find(communicationQueues, { endpointId: smsConfig.id });
      if (commQueue) {
        acc[smsConfig.id] = commQueue.name;
      }
      return acc;
    },
    {}
  );

  useEffect(() => {
    if (fetchError) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [fetchError, dispatch]);

  const smsEndpoints = useMemo(
    () =>
      smsConfigurations.map((sms: SmsConfig) => {
        const inboxField = communicationQueues
          .filter(({ endpointId }) => endpointId === sms.id)
          .map(({ routingGroupId }) => {
            const inbox = inboxes.find(({ id }) => id === routingGroupId);
            return get(inbox, 'name');
          })
          .join(',');
        return { ...sms, inbox: inboxField, name: sms.name || endpointsCommunicationQueueName[sms.id] };
      }),

    [communicationQueues, inboxes, smsConfigurations, endpointsCommunicationQueueName]
  );

  return {
    errors: smsErrors,
    items: smsEndpoints,
    loadingState: state,
    resetErrors: endpointsResource.actions.resetErrors,
  };
};
